import { Component, computed, effect, input, output, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";

import { NzAlertModule } from "ng-zorro-antd/alert";
import { NzButtonModule } from "ng-zorro-antd/button";
import { NzInputModule } from "ng-zorro-antd/input";

import { LifecycleConfiguration } from "../../../models/lifecycle-rule.types";

/** Result of parsing the textarea: the parsed value, or the reason it failed. */
type ParseResult =
  | { ok: true; value: unknown }
  | { ok: false; error: string };

/**
 * Raw-JSON editor for an S3 bucket's lifecycle configuration.
 *
 * The user edits the rules array as pretty-printed JSON text in a plain
 * textarea. The text is parsed on every keystroke; the Save button is disabled
 * whenever the JSON is invalid, and a parse error is shown inline. On save, the
 * parsed array is emitted via (save) for the parent to send to the backend.
 */
@Component({
  selector: "app-bucket-lifecycle-json-editor",
  imports: [FormsModule, NzAlertModule, NzButtonModule, NzInputModule],
  templateUrl: "./bucket-lifecycle-json-editor.component.html",
  styleUrl: "./bucket-lifecycle-json-editor.component.less",
})
export class BucketLifecycleJsonEditorComponent {
  /** Initial configuration to seed the editor with. */
  readonly configuration = input.required<LifecycleConfiguration>();

  /** Set true by the parent while the save request is in flight. */
  readonly saving = input<boolean>(false);

  /** Emits the parsed rules array when the user saves valid JSON. */
  readonly save = output<LifecycleConfiguration>();

  /** The raw text currently in the textarea. */
  protected readonly text = signal<string>("");

  constructor() {
    // Seed (and re-seed) the textarea whenever the input configuration changes,
    // e.g. when the user selects a different bucket. Runs once on init too.
    effect(() => this.text.set(this.serialized()));
  }

  /**
   * Attempt to parse the current text. Returns the parsed value on success or
   * an { error } object on failure — a single computed feeding both the
   * validity flag and the error message, so parsing happens once per change.
   */
  private readonly parsed = computed<ParseResult>(() => {
    const raw = this.text().trim();
    if (raw === "") {
      return { ok: false, error: "Configuration is empty." };
    }
    try {
      return { ok: true, value: JSON.parse(raw) };
    } catch (e) {
      return { ok: false, error: (e as Error).message };
    }
  });

  protected readonly isValid = computed(() => this.parsed().ok);

  protected readonly parseError = computed(() =>
    this.parsed().ok ? null : "error",
  );

  /** The input configuration as the pretty-printed JSON the textarea shows. */
  private readonly serialized = computed(() =>
    JSON.stringify(this.configuration() ?? [], null, 2),
  );

  /** Restore the textarea to the last configuration passed in. */
  protected reset(): void {
    this.text.set(this.serialized());
  }

  protected onSave(): void {
    const p = this.parsed();
    if (!p.ok) return; // button is disabled in this state, but guard anyway
    this.save.emit(p.value as LifecycleConfiguration);
  }
}
